// Time off (legacy api `modules/Scheduling` time-off actions; field notes register.dialog.my-schedule § Time Off,
// register.dialog.schedule.time-off-delete and backoffice.employees.time-off): an employee's request for whole days
// off, as location config (`labor.time_off_request`, ADR-015) so a register files, edits and cancels it at the store's
// node with the internet down and every register reads it from its synced config. Core's `labor/time-off` holds the
// rules (the notice window, the overlap guard, pending-only changes, the day counts). The Back Office lists, files,
// reviews and deletes them.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { EmployeeId, LocalDate, LocationId, TimeOffRequestId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten } from '../config/index.ts';
import { LocationTime } from './index.ts';

export const TimeOffStatus = z.enum(['pending', 'approved', 'denied']).meta({ id: 'TimeOffStatus' });

/** Who filed or reviewed it: the employee at a register, or a Back Office user. Null reviewer: not reviewed, or approved by the location's auto-approve. */
const TimeOffActor = z
  .object({ kind: z.enum(['staff', 'user']), id: z.uuid(), name: z.string().nullable() })
  .meta({ id: 'TimeOffActor' });

export const TimeOffRequestView = z
  .object({
    requestId: TimeOffRequestId,
    locationId: LocationId,
    employeeId: EmployeeId,
    employeeName: z.string().nullable(),
    startDate: LocalDate,
    endDate: LocalDate,
    /** Days from start to end, both included (core `timeOffDays`). */
    days: z.int().positive(),
    reason: z.string().nullable(),
    status: TimeOffStatus,
    requestedAt: LocationTime,
    createdBy: TimeOffActor,
    reviewedBy: TimeOffActor.nullable(),
    reviewedAt: LocationTime.nullable(),
    reviewerNotes: z.string().nullable(),
    version: z.int().nonnegative(),
  })
  .meta({ id: 'TimeOffRequestView' });

export const TimeOffRequestNotFound = defineError({
  code: 'labor.time_off_request_not_found', httpStatus: 404, message: 'errors.labor.time_off_request_not_found', retryable: false,
  details: z.object({ requestId: TimeOffRequestId }),
});

/** Core `TimeOffRefusal`: an end before the start, inside the notice window, overlapping the employee's own, or no longer pending. */
export const TimeOffRefused = defineError({
  code: 'labor.time_off_refused', httpStatus: 422, message: 'errors.labor.time_off_refused', retryable: false,
  details: z.object({ rule: z.enum(['end_before_start', 'notice', 'overlaps', 'not_pending']), noticeDays: z.int().nonnegative().optional() }),
});

const Window = { startDate: LocalDate, endDate: LocalDate, reason: z.string().trim().max(1000).nullable().optional() };

const atRegister = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: { session: 'device' }, risk: 'low', dryRun: false, agent: { expose: false },
} as const;

const inBackOffice = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], permission: 'labor.timecards.manage', risk: 'low', dryRun: false, agent: { expose: false },
} as const;

/**
 * My Schedule › Time Off › Submit Request (legacy `POST /v1/employee-time-off-requests`): the employee the Time Clock
 * identified by PIN asks for days off, at least the location's notice ahead. Approved at once when the location
 * auto-approves time off.
 */
export const RequestTimeOff = defineCommand({
  ...atRegister,
  name: 'labor.requestTimeOff',
  summary: 'Request time off for an employee at the register, at least the location\'s notice ahead.',
  input: z.object({ requestId: TimeOffRequestId, locationId: LocationId, employeeId: EmployeeId, ...Window }),
  output: TimeOffRequestView,
  errors: [TimeOffRefused, CloudAuthoritative],
  legacy: ['POST /v1/employee-time-off-requests'],
});

/** Edit Request › Save Changes (legacy `PUT /v1/employee-time-off-requests/{id}`): the employee's own pending request; it stays pending. */
export const UpdateTimeOffRequest = defineCommand({
  ...atRegister,
  name: 'labor.updateTimeOffRequest',
  summary: "Change the dates or reason of an employee's own pending time-off request.",
  input: z.object({ requestId: TimeOffRequestId, locationId: LocationId, employeeId: EmployeeId, ...Window }),
  output: TimeOffRequestView,
  errors: [TimeOffRequestNotFound, TimeOffRefused, CloudAuthoritative],
  legacy: ['PUT /v1/employee-time-off-requests/{id}'],
});

/** Delete Request (legacy `DELETE /v1/employee-time-off-requests/{id}`): the employee withdraws their own pending request. */
export const CancelTimeOffRequest = defineCommand({
  ...atRegister,
  name: 'labor.cancelTimeOffRequest',
  summary: "Withdraw an employee's own pending time-off request.",
  input: z.object({ requestId: TimeOffRequestId, locationId: LocationId, employeeId: EmployeeId }),
  output: TimeOffRequestView,
  errors: [TimeOffRequestNotFound, TimeOffRefused, CloudAuthoritative],
  legacy: ['DELETE /v1/employee-time-off-requests/{id}'],
});

/** Employees › Time Off › Add Time Off (legacy `POST /v1/time-off-requests`): a manager files for an employee; no notice is asked. */
export const AddTimeOff = defineCommand({
  ...inBackOffice,
  name: 'labor.addTimeOff',
  summary: 'File time off for an employee from the Back Office (no advance notice is required).',
  input: z.object({ requestId: TimeOffRequestId, locationId: LocationId, employeeId: EmployeeId, ...Window }),
  output: TimeOffRequestView,
  errors: [TimeOffRefused, CloudAuthoritative],
  legacy: ['POST /v1/time-off-requests'],
});

/** Review Time-Off Request › Approve / Deny (legacy `PUT /v1/time-off-requests/{id}`). */
export const ReviewTimeOffRequest = defineCommand({
  ...inBackOffice,
  name: 'labor.reviewTimeOffRequest',
  summary: 'Approve or deny a pending time-off request.',
  input: z.object({
    requestId: TimeOffRequestId, locationId: LocationId, decision: z.enum(['approved', 'denied']), notes: z.string().trim().max(1000).nullable().optional(),
  }),
  output: TimeOffRequestView,
  errors: [TimeOffRequestNotFound, TimeOffRefused, CloudAuthoritative],
  legacy: ['PUT /v1/time-off-requests/{id}'],
});

/** Employees › Time Off › Delete (legacy `DELETE /v1/time-off-requests/{id}`): a manager may delete any request, a decided one too. */
export const DeleteTimeOffRequest = defineCommand({
  ...inBackOffice,
  name: 'labor.deleteTimeOffRequest',
  summary: 'Delete a time-off request, whatever its status.',
  input: z.object({ requestId: TimeOffRequestId, locationId: LocationId }),
  output: TimeOffRequestView,
  errors: [TimeOffRequestNotFound, CloudAuthoritative],
  legacy: ['DELETE /v1/time-off-requests/{id}'],
});

/** Employees › Time Off (legacy `GET /v1/time-off-requests`): the location's requests, newest filed first, optionally one status. */
export const TimeOffRequests = defineQuery({
  name: 'labor.timeOffRequests',
  summary: "A location's time-off requests, newest first, optionally of one status.",
  input: z.object({ locationId: LocationId, status: TimeOffStatus.optional() }),
  output: z.object({ requests: z.array(TimeOffRequestView) }),
  permission: 'labor.timecards.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/time-off-requests', 'GET /v1/employee-time-off-requests'],
});

export const timeOffCommands = [RequestTimeOff, UpdateTimeOffRequest, CancelTimeOffRequest, AddTimeOff, ReviewTimeOffRequest, DeleteTimeOffRequest] as const;
export const timeOffQueries = [TimeOffRequests] as const;
